import React, { useEffect, useLayoutEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "react-router-dom";
import { ABOUT_SKILLS } from "../../data/aboutSkills";
import { HeroThreeScene } from "./HeroThreeScene";
import {
  CTAGroup,
  CapabilitiesLead,
  CapabilitiesTitle,
  CapabilityTile,
  CapabilitiesSection,
  FeaturedCard,
  FeaturedGrid,
  FeaturedSection,
  FeaturedSectionHead,
  HeroBackdrop,
  HeroIntroColumn,
  HeroMeta,
  HeroMetricsStrip,
  HeroName,
  HeroPanel,
  HeroRole,
  HeroShell,
  HeroThreeHost,
  HeroVisualColumn,
  OrbitRing,
  PillRow,
  PortfolioBadge,
  ProfileFrame,
  ProfileGlow,
  ProfileWrap,
  SectionLabel,
  SectionTitle,
  SkillsLabel,
  StatBarItem,
  StatsBar,
  Banner,
  HomeScene,
  LeadText,
  SceneGlow,
  NoiseLayer,
  GridOverlay,
} from "./styles";

gsap.registerPlugin(ScrollTrigger);

export default function Home() {
  const Img1 = require("../../assets/images/person2.png");
  const heroRef = useRef<HTMLDivElement | null>(null);

  const stats = [
    { value: "6+", label: "Years in XR Engineering" },
    { value: "15+", label: "Production Projects" },
    { value: "4", label: "Platforms (Web, Mobile, MR, VR)" },
  ];

  const capabilities = [
    {
      icon: "◈",
      title: "Spatial products",
      text: "End-to-end MR/AR/VR apps with robust UX for enterprise and consumer.",
    },
    {
      icon: "◎",
      title: "Realtime sync",
      text: "Multi-user sessions, shared state, and low-latency collaboration.",
    },
    {
      icon: "◇",
      title: "Industrial XR",
      text: "Digital twins, remote assist, and training on real hardware workflows.",
    },
    {
      icon: "✦",
      title: "Web & native 3D",
      text: "WebGL, WebXR, and device-native stacks tuned for performance.",
    },
  ];

  const featuredProjects = [
    {
      title: "Holo Work",
      description:
        "Collaborative industrial mixed reality platform for planning, training, and simulation.",
      route: "/HoloWork",
      tags: ["MR", "Realtime", "Industrial"],
    },
    {
      title: "AR Connect",
      description:
        "Remote multi-user AR support workflow with live spatial interaction and guidance.",
      route: "/ARConnect",
      tags: ["AR", "Multi-user", "Mobile"],
    },
    {
      title: "Robot Simulation",
      description:
        "Immersive robot training prototype with physics interaction and process simulation.",
      route: "/RobotSimulation",
      tags: ["VR", "Training", "Simulation"],
    },
  ];

  useEffect(() => {
    if (heroRef.current) {
      const targets = heroRef.current.querySelectorAll(".hero-reveal");
      gsap.fromTo(
        targets,
        { opacity: 0, y: 28, filter: "blur(6px)" },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 1,
          stagger: 0.1,
          ease: "power3.out",
          delay: 0.05,
        },
      );
    }
  }, []);

  useLayoutEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reducedMotion || !heroRef.current) return;

    const ctx = gsap.context(() => {
      gsap.utils.toArray<HTMLElement>(".cap-reveal").forEach((el, i) => {
        gsap.from(el, {
          scrollTrigger: {
            trigger: el,
            start: "top 88%",
            toggleActions: "play none none none",
          },
          y: 40,
          opacity: 0,
          duration: 0.8,
          delay: i * 0.07,
          ease: "power3.out",
        });
      });

      gsap.from(".featured-head-reveal", {
        scrollTrigger: {
          trigger: ".featured-section",
          start: "top 78%",
          toggleActions: "play none none none",
        },
        y: 32,
        opacity: 0,
        duration: 0.75,
        ease: "power3.out",
      });

      gsap.utils
        .toArray<HTMLElement>(".featured-card-animate")
        .forEach((el, i) => {
          gsap.from(el, {
            scrollTrigger: {
              trigger: ".featured-section",
              start: "top 82%",
              toggleActions: "play none none none",
            },
            y: 52,
            opacity: 0,
            duration: 0.9,
            delay: 0.15 + i * 0.14,
            ease: "power3.out",
          });
        });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <HomeScene className="page-wrapper">
      <HeroBackdrop aria-hidden>
        <SceneGlow />
        <GridOverlay className="motion-grid" />
        <NoiseLayer />
      </HeroBackdrop>
      <Banner className="pt-0 pt-sm-4">
        <div className="container" ref={heroRef}>
          <HeroPanel>
            <HeroShell>
              <div className="row g-4 g-lg-5 align-items-start m-0">
                <HeroIntroColumn className="col-12 col-lg-7">
                  <PortfolioBadge className="hero-reveal">
                    XR portfolio · immersive products
                  </PortfolioBadge>

                  <HeroName className="hero-reveal">
                    Bharath Chandra Reddy Vadde
                  </HeroName>

                  <HeroRole className="hero-reveal">
                    Senior augmented, mixed reality, and realtime 3D developer
                  </HeroRole>

                  <LeadText className="hero-reveal">
                    I ship XR products with realtime collaboration, industrial
                    digital twins, and immersive training—production-ready
                    across HoloLens, mobile AR, WebXR, and desktop 3D.
                  </LeadText>

                  <CTAGroup className="hero-reveal">
                    <Link to="/Projects" className="primary-cta">
                      Explore projects
                    </Link>
                    <Link to="/Contact" className="secondary-cta">
                      Hire me for XR
                    </Link>
                  </CTAGroup>
                </HeroIntroColumn>

                <HeroVisualColumn className="col-12 col-lg-5">
                  <ProfileWrap className="hero-reveal">
                    <HeroThreeHost>
                      <HeroThreeScene />
                    </HeroThreeHost>
                    <OrbitRing />
                    <OrbitRing className="two" />
                    <ProfileGlow />
                    <ProfileFrame>
                      <img
                        src={Img1}
                        alt="Bharath Chandra Reddy Vadde"
                        className="w-100"
                      />
                    </ProfileFrame>
                  </ProfileWrap>
                  <HeroMeta className="hero-reveal">
                    Available for senior XR product engineering and consulting.
                  </HeroMeta>
                </HeroVisualColumn>
              </div>

              <HeroMetricsStrip className="hero-reveal">
                <StatsBar>
                  {stats.map((stat) => (
                    <StatBarItem key={stat.label}>
                      <div className="value">{stat.value}</div>
                      <div className="label">{stat.label}</div>
                    </StatBarItem>
                  ))}
                </StatsBar>

                <div>
                  <SkillsLabel>Core stack</SkillsLabel>
                  <PillRow>
                    {ABOUT_SKILLS.map((item) => (
                      <span key={item.id}>{item.title}</span>
                    ))}
                  </PillRow>
                </div>
              </HeroMetricsStrip>
            </HeroShell>
          </HeroPanel>

          {/* <HomeShadowDemoSection className="animate__animated animate__fadeIn">
            <HomeShadowDemoHeader>
              <div>
                <SectionLabel>Real-time 3D</SectionLabel>
                <SectionTitle style={{ maxWidth: "none" }}>
                  Interactive shadow map
                </SectionTitle>
              </div>
              <Link to="/3D" className="home-shadow-demo-link">
                Fullscreen demo →
              </Link>
            </HomeShadowDemoHeader>
            <ProjectsShadowWrap>
              <ProjectsShadowStage variant="embed" />
              <p className="text-center small mt-2 mb-0 px-2" style={{ color: "rgba(148,163,184,0.9)" }}>
                Drag to orbit · directional + spot lights with soft shadows
              </p>
            </ProjectsShadowWrap>
          </HomeShadowDemoSection> */}

          <CapabilitiesSection>
            <CapabilitiesLead className="cap-reveal">
              <SectionLabel>What I deliver</SectionLabel>
              <CapabilitiesTitle>
                Spatial software, realtime systems, and deployable XR for teams.
              </CapabilitiesTitle>
            </CapabilitiesLead>
            {capabilities.map((cap) => (
              <CapabilityTile key={cap.title} className="cap-reveal">
                <div className="cap-icon" aria-hidden>
                  {cap.icon}
                </div>
                <h4>{cap.title}</h4>
                <p>{cap.text}</p>
              </CapabilityTile>
            ))}
          </CapabilitiesSection>

          <FeaturedSection className="featured-section">
            <FeaturedSectionHead className="featured-head-reveal">
              <SectionLabel>Featured Case Studies</SectionLabel>
              <SectionTitle>
                Real products, real deployment impact, immersive user outcomes.
              </SectionTitle>
            </FeaturedSectionHead>
            <FeaturedGrid>
              {featuredProjects.map((project, i) => (
                <FeaturedCard
                  key={project.title}
                  to={project.route}
                  className="featured-card-animate"
                >
                  <span className="featured-card-index" aria-hidden>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                  <div className="tag-row">
                    {project.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                  <span className="featured-cta">View case study</span>
                </FeaturedCard>
              ))}
            </FeaturedGrid>
          </FeaturedSection>
        </div>
      </Banner>
    </HomeScene>
  );
}
